import { ChangeDetectorRef, Component, computed, effect, inject, signal } from '@angular/core';
import { getAssetURL } from 'src/app/constants/assets';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { ItemAsset } from 'src/app/interfaces/item-asset';
import { ItemAssetsService } from 'src/app/services/item-assets/item-assets.service';
import { ItemAssetLocationToThreeJSKeyMap } from 'src/app/utils/item-asset.util';
import { assert, assertTrue } from 'src/app/utils/assert.util';
import * as THREE from 'three';
import { CSS2DObject, CSS2DRenderer, EXRLoader, GLTF, RGBELoader } from 'three-stdlib';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { FormsService } from 'src/app/services/forms/forms.service';
import { MaterialsService } from 'src/app/services/materials/materials.service';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { AdminToolWidthMapping } from 'src/app/interfaces/admin-tool-width-mapping';
import { Material } from 'src/app/interfaces/material';
import { DesignsService } from 'src/app/services/designs/designs.service';
import { Form } from 'src/app/interfaces/form';
import { WebProviderService } from 'src/app/providers/web/web.provider.service';
import { AdminToolNSWidthMappingResponse } from 'src/app/interfaces/admin-tool-ns-width-mapping-response';
import { AdminToolFormModel } from 'src/app/interfaces/admin-tool-form-model';
import { environment } from 'src/environments/environment';
import { Width } from 'src/app/interfaces/width';

@Component({
  selector: 'app-admin-controls-texture-check',
  templateUrl: './admin-controls-texture-check.component.html',
  styleUrl: './admin-controls-texture-check.component.scss'
})
export class AdminControlsTextureCheckComponent {
  private itemAssetsService = inject(ItemAssetsService);
  private loadingService = inject(LoadingService);
  private formsService = inject(FormsService);
  private materialsService = inject(MaterialsService);
  private widthsService = inject(WidthsService);
  private designsService = inject(DesignsService);
  private webProvider = inject(WebProviderService);

  designs = computed(() => this.designsService.values());
  forms = computed(() => this.formsService.values());
  insideMaterials = computed(() => this.materialsService.values().filter(m => m.type === 'Primary'));
  outsideMaterials = computed(() => this.materialsService.values().filter(m => m.type === 'Secondary'));
  widths = computed(() => this.widthsService.values().sort((a, b) => a.value - b.value));
  newTextureWidth = signal<number | null>(null);
  newRingWidth = signal<number | null>(null);
  form = signal<Form | null>(null);
  insideMaterial = signal<Material | null>(null);
  outsideMaterial = signal<Material | null>(null);
  widthMappings = signal<AdminToolWidthMapping[]>([]);
  textures = signal<File[]>([]);
  lastYPosition = 0;
  labelRenderer: CSS2DRenderer;
  changeQueue: (() => Promise<void>)[] = [];
  isChangeQueueExecuting = false;
  textureWidthMappingChanges = signal(new Map<number, number>);
  ringWidthMappingChanges = signal(new Map<number, number>);

  constructor() {
    (window as any).CSS2DObject = CSS2DObject;
    (window as any).CSS2DRenderer = CSS2DRenderer;
    const mainImageComponent = (window as any).mainImageComponent;

    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(mainImageComponent.imgCanvas().nativeElement.clientWidth, mainImageComponent.imgCanvas().nativeElement.clientHeight);
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0px';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    mainImageComponent.imgCanvas().nativeElement.parentElement.appendChild(this.labelRenderer.domElement);

    effect(async () => {
      const form = this.form();
      if (form) {
        this.loadingService.load('admingtools::load-width-mappings');
        const nsWidthMappings = await this.getTextureRingWidthMappings();
        const mappingsForProfile = nsWidthMappings?.filter(m => m.profile_short_name === form?.shortName);
        const newWidthMappings = (mappingsForProfile?.map(m => ({ textureWidth: +m.mapped_width.replace('MM', ''), ringWidth: +m.width })) ?? []).sort((a,b) => a.ringWidth - b.ringWidth);
        this.widthMappings.set(newWidthMappings);
        this.loadingService.done('admingtools::load-width-mappings');
      }
    }, { allowSignalWrites: true });

    // effect(() => {
    //   const newTextureWidth = this.newTextureWidth();
    //   const newRingWidth = this.newRingWidth();
    //   if (newTextureWidth && newRingWidth) {
    //     this.widthMappings.update((w) => w.concat({ textureWidth: newTextureWidth, ringWidth: newRingWidth }));
    //     setTimeout(() => {
    //       this.newTextureWidth.set(null);
    //       this.newRingWidth.set(null);
    //     }, 10);
    //   }
    // }, { allowSignalWrites: true });

    effect(() => {
      const form = this.form();
      const insideMaterial = this.insideMaterial();
      const outsideMaterial = this.outsideMaterial();
      const widthMappings = this.widthMappings();
      const textures = this.textures();
      const widths = this.widths();

      if (form && insideMaterial && outsideMaterial && widthMappings.length > 0 && textures.length > 0) {
        this.scheduleChange(async () => {
          await this.doModelStuff(form, insideMaterial, outsideMaterial, widthMappings, textures, widths);
        });
      }
    }, { allowSignalWrites: true });
  }

  scheduleChange(handler: () => Promise<void>) {
    console.log(this.isChangeQueueExecuting)
    this.changeQueue.push(handler);
    if (!this.isChangeQueueExecuting) {
      this.isChangeQueueExecuting = true;
      this.doChangeQueueWork();
    }
  }

  async doChangeQueueWork() {
    while(this.changeQueue.length > 0) {
      console.log('is change executing', this.isChangeQueueExecuting)
      console.log('start render')
      const change = this.changeQueue.shift();
      await change?.call(this);
      console.log('end render')
    }
    this.isChangeQueueExecuting = false;
  }

  async doModelStuff(form: Form, insideMaterial: Material, outsideMaterial: Material, widthMappings: AdminToolWidthMapping[], textures: File[], widths: Width[]) {
    const mainImageComponent = (window as any).mainImageComponent;
    console.log('clear')
    mainImageComponent.scene.clear();
    mainImageComponent.renderer.setAnimationLoop(null);
    this.labelRenderer.domElement.innerHTML = '';
    mainImageComponent.scene.visible = false;
    this.lastYPosition = 0;
    mainImageComponent.orbitControls.enablePan = true;
    mainImageComponent.orbitControls.maxPolarAngle = Infinity;
    mainImageComponent.orbitControls.minPolarAngle = -Infinity;
    mainImageComponent.orbitControls.maxAzimuthalAngle = Infinity;
    mainImageComponent.orbitControls.minAzimuthalAngle = -Infinity;
    mainImageComponent.orbitControls.update();

    this.loadingService.load('admin-tools::add-models');

    const formModels = await this.getFormModels(form.id);
    formModels?.sort((a, b) => +a.width.toLowerCase().replace('mm', '') - +b.width.toLowerCase().replace('mm', ''))

    assert(formModels, 'No form models');

    const configuredItems: ConfiguredItem[] = formModels.map(fm => ({
      design: this.designs().find(d => d.shortName === '010') ?? null,
      form: form,
      primaryMaterial: insideMaterial,
      secondaryMaterial: outsideMaterial,
      width: widths.find(w => w.displayName === fm.width) ?? null,
      engravingType: null,
      engravingText: null,
      size: null,
      gemstoneType: null,
      gemstoneArrangement: null,
      gemstoneSize: null,
      gemstoneSetting: null,
      gemstoneShape: null
    }));

    const allItemAssets = await this.getItemAssets(configuredItems);

    for (let i = 0; i < configuredItems.length; i++) {
      const formModel = formModels[i];
      const configuredItem = configuredItems[i];
      const width = configuredItem.width;

      assert(width, 'Could not match width');

      const textureWidthToUse = widthMappings.find(mapping => mapping.ringWidth >= width.value)?.textureWidth.toString().replace('.', '_');
      assert(textureWidthToUse, 'No texture width found');

      const texturesToUse = textures.filter(texture => texture.name.toLowerCase().includes('-general') || texture.name.toLowerCase().includes(`-${textureWidthToUse}mm-`))

      const itemAssetsFromTextures = texturesToUse.map(image => ({
        fileName: image.name,
        type: this.getTextureTypeFromName(image.name),
        location: 'Outside',
        fileURL: URL.createObjectURL(image)
      }));

      const itemAssets = allItemAssets[i];
      const itemAssetsToUse = itemAssets.filter(asset => asset.location !== 'Outside').concat(itemAssetsFromTextures);

      await this.addModel(configuredItem, itemAssetsToUse, formModel);
    }

    const boundingBox = new THREE.Box3();
    boundingBox.setFromObject(mainImageComponent.scene);
    const boundingSphere = new THREE.Sphere();
    boundingBox.getBoundingSphere(boundingSphere);

    assert(mainImageComponent.camera, 'No camera');
    const fov = mainImageComponent.camera.fov * Math.PI / 180;
    const size = new THREE.Vector3();
    boundingBox.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    const z = (Math.abs(maxDim / 4 * Math.tan(fov * 2))) * 0.6;

    const minZ = boundingBox.min.z;
    const farE = (minZ < 0) ? -minZ + z : z - minZ;

    assert(mainImageComponent.orbitControls, 'No orbit controls');

    mainImageComponent.camera.position.set(0, 0, 0);

    mainImageComponent.camera.far = 1000;
    mainImageComponent.camera.updateProjectionMatrix();
    const center = new THREE.Vector3();
    boundingBox.getCenter(center);
    mainImageComponent.orbitControls.target.set(center.x, center.y, center.z);
    mainImageComponent.orbitControls.update();

    // const positionScale = farE * 2 / farE;
    mainImageComponent.camera.position.multiplyScalar(farE * 2);
    mainImageComponent.camera.lookAt(center);

    mainImageComponent.orbitControls.maxDistance = farE;
    mainImageComponent.orbitControls.update();
    mainImageComponent.orbitControls.setAzimuthalAngle(90 * Math.PI / 180)
    mainImageComponent.orbitControls.enablePan = true;
    mainImageComponent.orbitControls.enableZoom = true;
    mainImageComponent.orbitControls.minDistance = 0;

    mainImageComponent.scene.visible = true;

    mainImageComponent.renderer.setAnimationLoop(this.newThreeAnimate.bind(this));
    this.loadingService.done('admin-tools::add-models');
  }

  newThreeAnimate() {
    this.labelRenderer.render((window as any).mainImageComponent.scene, (window as any).mainImageComponent.camera);
    (window as any).mainImageComponent.threeAnimate()
  }

  textureFilesChange(event: Event) {
    this.textures.set(Array.from((event.target as HTMLInputElement).files ?? []))
  }

  async getItemAssets(configuredItems: ConfiguredItem[]) {
    const itemAssets = await this.fetchFirstWorkingItemAssets(configuredItems);
    if (itemAssets) {
      return configuredItems.map(_ => itemAssets);
    }

    const newConfiguredItems = configuredItems.map(c => Object.assign({}, c, { primaryMaterial: this.materialsService.values().find(v => v.shortName === c.secondaryMaterial?.shortName && v.type === 'Secondary') ?? null }));
    const secondTryItemAssets = await this.fetchFirstWorkingItemAssets(newConfiguredItems);
    if (secondTryItemAssets) {
      return configuredItems.map(_ => secondTryItemAssets);
    }

    const lastTryConfiguredItems = configuredItems.map(c => Object.assign({}, c, { secondaryMaterial: this.materialsService.values().find(v => v.type === 'Secondary' && v.shortName === '14W') ?? null, primaryMaterial: this.materialsService.values().find(v => v.type === 'Primary' && v.shortName === '14W') ?? null }));
    const lastTryItemAssets = await this.fetchFirstWorkingItemAssets(lastTryConfiguredItems);
    if (lastTryItemAssets) {
      return configuredItems.map(_ => lastTryItemAssets);
    }

    throw 'No item assets found';
  }

  async fetchFirstWorkingItemAssets(configuredItems: ConfiguredItem[]) {
    for (let i = 0; i < configuredItems.length; i++) {
      const configuredItem = configuredItems[i];
      try {
        const itemAssets = await this.itemAssetsService.get(configuredItem);
        if (itemAssets.length > 0) {
          return itemAssets;
        }
        return null;
      } catch(e) {}
    }
    return null;
  }

  async addModel(configuredItem: ConfiguredItem, itemAssets: ItemAsset[], formModel: AdminToolFormModel) {
    const mainImageComponent = (window as any).mainImageComponent;
    const imgCanvasElement = mainImageComponent.imgCanvas()?.nativeElement;
    const overlayButtons = mainImageComponent.overlayButtons();

    if (imgCanvasElement && overlayButtons) {
      assert(mainImageComponent.renderer, 'Renderer was null');

      try {
        const model = await this.createModel(configuredItem, itemAssets, formModel.model);

        const modelGroup = new THREE.Group();
        modelGroup.name = 'model group'
        modelGroup.add(model.scene);

        mainImageComponent.renderer.setClearColor( 0xffffff, 0);

        model.scene.updateMatrixWorld();

        const boundingBoxes: THREE.Box3[] = [];
        model.scene.traverse((child: any) => {
          if (child.isMesh) {
            const boundingBox = new THREE.Box3();
            boundingBox.setFromObject(child);
            boundingBoxes.push(boundingBox);
          }
        });

        const sizes = boundingBoxes.map((boundingBox) => {
          const size = new THREE.Vector3()
          boundingBox.getSize(size);
          return size;
        });

        const dim = sizes.reduce((max, size) => {
          return Math.max(max, size.x, size.y, size.z);
        }, 0);

        const originalModelSizeVector = new THREE.Vector3(dim, dim, dim);
        const modelSizeVector = new THREE.Vector3(originalModelSizeVector.x, originalModelSizeVector.y, originalModelSizeVector.z);
        modelSizeVector.multiplyScalar(2);
        modelSizeVector.x *= 1.2;

        assert(mainImageComponent.scene, 'No scene');
        mainImageComponent.scene.add(modelGroup);

        const modelCenter = new THREE.Vector3();
        boundingBoxes[0].getCenter(modelCenter);

        const labelElement = document.createElement('div');
        labelElement.innerHTML = `<span>${configuredItem.width?.displayName}</span>`;
        labelElement.style.border = '1px solid black';
        labelElement.style.padding = '0.5rem';

        const label = new CSS2DObject(labelElement);
        label.position.set(boundingBoxes[0].min.x - 10, modelCenter.y, boundingBoxes[0].max.z + 5);
        modelGroup.add(label);
      } catch (e) {
        console.error(e);
      }
    }
  }

  getTextureTypeFromName(name: string) {
    if (name.includes('-C-')) {
      return 'Base Color Texture';
    }

    if (name.includes('-N-')) {
      return 'Normal Texture';
    }

    if (name.includes('-R-')) {
      return 'Metallic/Roughness Texture';
    }

    if (name.includes('-D-')) {
      return 'Displacement Texture';
    }

    if (name.includes('-A-')) {
      return 'Ambient Occlusion Texture';
    }

    throw 'Cannot determine texture type';
  }

  async createModel(configuredItem: ConfiguredItem, itemAssets: ItemAsset[], modelFileName: string) {
    const mainImageComponent = (window as any).mainImageComponent;
    const textureLoader = new THREE.TextureLoader();

    const model: any = await new Promise((res, rej) => mainImageComponent.gltfLoader.load(getAssetURL(`${modelFileName}`), res, undefined, rej));
    model.scene.children[0].scale.set(1,1,1);
    const modelSceneBoundingBox = new THREE.Box3();
    modelSceneBoundingBox.setFromObject(model.scene);
    const modelSceneSize = new THREE.Vector3();
    modelSceneBoundingBox.getSize(modelSceneSize);
    model.scene.scale.set((23.8 / modelSceneSize.x), 1, (23.8 / modelSceneSize.x));
    model.scene.position.y += this.lastYPosition
    this.lastYPosition += 20;

    assert(configuredItem.primaryMaterial, 'No primary material');
    assert(configuredItem.design, 'No design');

    const modelMeshes: any[] = [];
    model.scene.traverse((descendant: any) => {
      if (descendant.isMesh) {
        const material = new descendant.material.constructor();
        material.copy(descendant.material);
        descendant.material.dispose();
        descendant.material = material;
        modelMeshes.push(descendant);
      }
    });

    const edgeIndex = modelMeshes.length === 2 ? null : 1;
    const outsideIndex = 0;
    const insideIndex = modelMeshes.length === 2 ? 1 : 2;

    model.scene.updateMatrixWorld(true);
    modelMeshes.forEach((mesh) => {
      const actualModelSize = new THREE.Vector3();
      const actualBoundingBox = new THREE.Box3();
      actualBoundingBox.setFromObject(mesh)
      actualBoundingBox.getSize(actualModelSize);
      mesh.geometry.computeBoundingBox();
      const targetSize = new THREE.Vector3();
      mesh.geometry.boundingBox.getSize(targetSize);
      const targetMin = Math.min(targetSize.x, targetSize.y, targetSize.z);
      const actualMin = Math.min(actualModelSize.x, actualModelSize.y, actualModelSize.z);
      const scaleFactor = targetMin / actualMin;
      mesh.scale.multiplyScalar(scaleFactor);
      mesh.updateMatrixWorld(true);
    });

    const indexMap = ['Outside'];
    if (modelMeshes.length === 3) {
      indexMap.push('Edge');
    }
    indexMap.push('Inside');

    const maxAnisotropy = mainImageComponent.renderer?.capabilities.getMaxAnisotropy() ?? 1;

    await Promise.all(modelMeshes.map(async (mesh, i) => {
      const location = indexMap[i];
      const texturesForThisLocation = itemAssets.filter(ia => ia.location === location);

      mesh.material.roughness = 1;
      mesh.material.metalness = 1;
      mesh.material.displacementScale = -1;
      mesh.material.normalMapType = THREE.TangentSpaceNormalMap;

      await Promise.all(texturesForThisLocation.map(async (texture) => {
        const textureFile = await textureLoader.loadAsync((texture as any).fileURL ?? getAssetURL(texture.fileName));
        const materialTextureKey = [ItemAssetLocationToThreeJSKeyMap[texture.type]].flat();
        materialTextureKey.forEach(key => {
          mesh.material[key] = textureFile;
          mesh.material[key].anisotropy = maxAnisotropy;
          mesh.material[key].needsUpdate = true;
        });
      }));
    }));

    modelMeshes[insideIndex].material.color.setHex(+configuredItem.primaryMaterial.color);
    if (edgeIndex) {
      modelMeshes[edgeIndex].material.color.setHex(+configuredItem.primaryMaterial.color);
    }

    if (configuredItem.secondaryMaterial) {
      modelMeshes[outsideIndex].material.color.setHex(+configuredItem.secondaryMaterial.color);
    }

    return model;
  }

  textureWidthMappingChanged(value: number, index: number) {
    this.textureWidthMappingChanges.update(tw => {
      tw.set(index, value);
      return tw;
    });
  }

  ringWidthMappingChanged(value: number, index: number) {
    this.ringWidthMappingChanges.update(rw => {
      rw.set(index, value);
      return rw;
    });
  }

  async getTextureRingWidthMappings() {
    const { mappingsURL, mappingsHeaders, mappingsQuery } = (window as any);
    if (mappingsURL && mappingsHeaders && mappingsQuery) {
      const response = await this.webProvider.post<AdminToolNSWidthMappingResponse[]>(mappingsURL, JSON.parse(mappingsQuery), { headers: mappingsHeaders});
      return response;
    }
    return null;
  }

  async getFormModels(formID: number) {
    const response = await this.webProvider.get<AdminToolFormModel[]>(`${environment.apiURL}/api/ItemAssets/FormModelAssets?formID=${formID}`);
    return response;
  }

  widthSaveButtonClick() {
    this.widthMappings.update(w => {
      const newArray = w.slice();

      this.ringWidthMappingChanges().forEach((value, key) => {
        newArray[key].ringWidth = +value;
      });

      this.textureWidthMappingChanges().forEach((value, key) => {
        newArray[key].textureWidth = +value;
      });

      newArray.sort((a, b) => a.ringWidth - b.ringWidth);
      return newArray;
    });

    this.ringWidthMappingChanges.update(rw => {
      rw.clear();
      return rw;
    });

    this.textureWidthMappingChanges.update(rw => {
      rw.clear();
      return rw;
    });

    const newTextureWidth = this.newTextureWidth();
    const newRingWidth = this.newRingWidth();
    if (newTextureWidth && newRingWidth) {
      this.widthMappings.update((w) => w.concat({ textureWidth: newTextureWidth, ringWidth: newRingWidth }));
      setTimeout(() => {
        this.newTextureWidth.set(null);
        this.newRingWidth.set(null);
      }, 10);
    }
  }
}
